import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Flex,
  Input,
  Text,
} from '@chakra-ui/react';

import { isDefined } from '@enterprise/core';

import type { ConfirmationDialogProps } from './types/confirmation-dialog.props';

import { useConfirmationDialogConfiguration } from './hooks/confirmation-dialog-configuration.hook';

/**
 * ConfirmationDialog component displays a modal dialog for user confirmation.
 *
 * It supports customizable title, description, icon, and confirmation text.
 * Optionally, it can require the user to type a confirmation phrase before enabling the confirm action.
 * The dialog can be triggered programmatically or via a trigger element.
 *
 * @param cancelLabel - Label for the cancel button (default: 'Cancel').
 * @param colorScheme - Chakra UI color scheme for accent styling.
 * @param colorShade - Shade of the color scheme for accent styling (default: '500').
 * @param confirm - If true, requires user to type confirmation text before confirming.
 * @param confirmationText - Text the user must type to confirm (default: 'confirm').
 * @param confirmLabel - Label for the confirm button (default: 'Confirm').
 * @param description - Description or content displayed in the dialog body.
 * @param icon - Optional icon displayed in the dialog header.
 * @param isLoading - If true, disables buttons and shows loading state.
 * @param onCancel - Callback invoked when the cancel button is clicked.
 * @param onConfirm - Callback invoked when the confirm button is clicked.
 * @param title - Title displayed in the dialog header.
 * @param trigger - Optional render prop for a trigger element to open the dialog.
 *
 * @returns A Chakra UI AlertDialog component for confirmation actions.
 */
export const ConfirmationDialog = ({
  cancelLabel = 'Cancel',
  colorScheme,
  colorShade = '500',
  confirm = false,
  confirmationText = 'confirm',
  confirmLabel = 'Confirm',
  defaultIsOpen = false,
  description,
  icon,
  isLoading = false,
  onCancel,
  onConfirm,
  title,
  trigger,
}: ConfirmationDialogProps) => {
  const { cancelRef, confirmed, handleConfirmationChange, isOpen, onClose, onOpen } =
    useConfirmationDialogConfiguration({
      confirmationText,
      defaultIsOpen,
    });
  const accentColor = `${colorScheme}.${colorShade}`;

  return (
    <>
      {isDefined(trigger) && trigger({ onOpen })}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader
              borderTopColor={accentColor}
              borderTopLeftRadius="md"
              borderTopRightRadius="md"
              borderTopWidth={5}
              fontSize="lg"
              fontWeight="bold"
              position="relative">
              {isDefined(icon) && (
                <Flex
                  alignItems="center"
                  bg={accentColor}
                  color="white"
                  h={16}
                  justifyContent="center"
                  left="50%"
                  p={3}
                  position="absolute"
                  rounded="full"
                  top="0"
                  transform="translateX(-50%) translateY(-50%)"
                  w={16}>
                  {icon}
                </Flex>
              )}
              <Text mt={4}>{title}</Text>
            </AlertDialogHeader>

            <AlertDialogBody>
              <Flex direction="column">
                {description}
                {confirm && (
                  <>
                    <Flex
                      justifyContent="center"
                      mt={4}>
                      Please type
                      <Text
                        as="span"
                        fontWeight="bold"
                        mx={1}>
                        {confirmationText}
                      </Text>
                      to confirm.
                    </Flex>
                    <Input
                      mt={1}
                      onChange={({ target: { value } }) => {
                        handleConfirmationChange(value);
                      }}
                      size="md"
                      textAlign="center"
                      w="full"
                    />
                  </>
                )}
              </Flex>
            </AlertDialogBody>

            <AlertDialogFooter
              borderBottomLeftRadius="md"
              borderBottomRightRadius="md"
              gap={3}>
              <Button
                isDisabled={isLoading}
                onClick={() => {
                  onClose();
                  onCancel?.();
                }}
                ref={cancelRef}>
                {cancelLabel}
              </Button>
              <Button
                colorScheme={colorScheme}
                isDisabled={isLoading || (confirm && !confirmed)}
                onClick={() => {
                  return onConfirm?.(onClose);
                }}>
                {confirmLabel}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
